import React, { useState } from 'react';

import { EventType } from '../types';
import { XMarkIcon } from '@heroicons/react/24/outline';

const Gallery: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'advocacy':
        return 'bg-blue-100 text-blue-800';
      case 'education':
        return 'bg-green-100 text-green-800';
      case 'cultural':
        return 'bg-purple-100 text-purple-800';
      case 'legal':
        return 'bg-red-100 text-red-800';
      case 'workplace':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const events: EventType[] = [
    {
      id: 1,
      title: 'Black History Month Celebration 2024',
      date: '2025-02-20',
      location: 'Calgary',
      description: 'Annual celebration of Black Canadian heritage and contributions to society.',
      image: "/assets/blackhistory.jpg",
      category: 'cultural'
    },
    {
      id: 2,
      title: 'Legal Rights Workshop',
      date: '2025-01-15',
      location: 'Calgary',
      description: 'Educational workshop on legal rights and advocacy for Black Canadians.',
      image: "/assets/legal-rights.jpg",
      category: 'legal'
    },
    {
      id: 3,
      title: 'Youth Mentorship Program Launch',
      date: '2024-12-10',
      location: 'Calgary',
      description: 'Launch of our new mentorship program connecting young professionals with experienced leaders.',
      image: '/assets/youth-mentorship.jpg',
      category: 'education'
    },
    {
      id: 4,
      title: 'Workplace Diversity Summit',
      date: '2024-04-05',
      location: 'Calgary',
      description: 'Summit focusing on diversity, inclusion, and equality in Canadian workplaces.',
      image: '/assets/workplace-diversity.jpg',
      category: 'workplace'
    },
    {
      id: 5,
      title: 'Community Advocacy Forum',
      date: '2024-05-20',
      location: 'Calgary',
      description: 'Forum discussing community advocacy strategies and policy recommendations.',
      image: '/assets/community-advocacy.jpg',
      category: 'advocacy'
    },
    {
      id: 6,
      title: 'Cultural Arts Festival',
      date: '2024-06-15',
      location: 'Calgary',
      description: 'Celebration of Black Canadian arts, music, and cultural expression.',
      image: '/assets/cultural-arts.jpg',
      category: 'cultural'
    }
  ];  const categories = [
    { value: 'all', label: 'All Events' },
    { value: 'advocacy', label: 'Advocacy' },
    { value: 'education', label: 'Education' },
    { value: 'cultural', label: 'Cultural' },
    { value: 'legal', label: 'Legal' },
    { value: 'workplace', label: 'Workplace' }
  ];

  const filteredEvents = selectedCategory === 'all' 
    ? events 
    : events.filter(event => event.category === selectedCategory);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 text-white bg-gradient-to-r from-primary-800 to-primary-900">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="mb-6 text-4xl font-bold md:text-5xl font-heading">
              Event Gallery
            </h1>
            <p className="max-w-3xl mx-auto text-xl text-gray-200 md:text-2xl">
              Explore the moments that define our community's journey toward equality and empowerment.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-3 rounded-full font-medium transition-colors duration-200 ${
                  selectedCategory === category.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-primary-50 hover:text-primary-700'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {filteredEvents.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-xl text-gray-600">No events found for the selected category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  className="overflow-hidden transition-shadow duration-300 bg-white shadow-lg cursor-pointer rounded-xl hover:shadow-xl"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="object-cover w-full h-48"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryStyle(event.category)}`}>
                        {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(event.date)}
                      </span>
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-gray-900">
                      {event.title}
                    </h3>
                    <p className="mb-3 text-gray-600">
                      {event.location}
                    </p>
                    <p className="text-gray-700 line-clamp-3">
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Event Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-2xl overflow-y-auto bg-white rounded-xl max-h-90vh">
            <div className="relative">
              <img
                src={selectedEvent.image}
                alt={selectedEvent.title}
                className="object-cover w-full h-64 rounded-t-xl"
              />
              <button
                onClick={() => setSelectedEvent(null)}
                className="absolute p-2 transition-colors bg-white rounded-full top-4 right-4 hover:bg-gray-100"
                aria-label="Close modal"
              >
                <XMarkIcon className="w-6 h-6 text-gray-700" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryStyle(selectedEvent.category)}`}>
                  {selectedEvent.category.charAt(0).toUpperCase() + selectedEvent.category.slice(1)}
                </span>
                <span className="text-gray-500">
                  {formatDate(selectedEvent.date)}
                </span>
              </div>
              <h2 className="mb-3 text-2xl font-bold text-gray-900">
                {selectedEvent.title}
              </h2>
              <p className="mb-4 text-lg text-gray-600">
                📍 {selectedEvent.location}
              </p>
              <p className="leading-relaxed text-gray-700">
                {selectedEvent.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
